import { baseUrl, httpClient } from '../providers/AppDataProvider';

const API_PATH = `${baseUrl}/vault/official-vaults`;

/** C-08 status machine. Only REQUESTED is written by admin_gql; the rest by the treasury. */
export type OfficialVaultStatus =
  | 'REQUESTED'
  | 'ACCOUNT_READY'
  | 'CATALOGUED'
  | 'ACTIVATED'
  | 'PENDING_REVIEW'
  | 'OPEN'
  | 'CANCELLED'
  | 'FAILED';

/** C-06 VaultApplicationReview (XBIT-4194): the decision, written once on the record. */
export interface VaultApplicationReview {
  outcome: 'APPROVED' | 'REJECTED';
  /** Admin id of the reviewer, from the JWT. */
  actor: string;
  at: string;
  raisingStartedAt?: string | null;
  raisingInceptionAt?: string | null;
  reason?: string | null;
}

export interface OfficialVaultRequestFailure {
  step: string;
  reason: string;
}

/** C-06 §2 `OfficialVaultRequest`. Ratios are 6-dp FRACTION strings (`"0.200000"` = 20%). */
export interface OfficialVaultRequest {
  vaultId: string;
  name: string;
  strategy: string;
  feeRate: string;
  lockupDays: number;
  xHandle?: string | null;
  groupChatId?: string | null;
  ownershipFloor: string;
  status: OfficialVaultStatus;
  /** Set only with FAILED. */
  failure?: OfficialVaultRequestFailure | null;
  /** Admin id (UUID) from the JWT — informational; the audit log is authoritative. */
  requestedBy: string;
  /** XBIT-4320: the admin's display name, resolved by the BFF; null (or absent from an older BFF) when unknown. */
  requestedByName?: string | null;
  requestedAt: string;
  updatedAt: string;
  /** C-08 v2; a record written before v2 reads OFFICIAL with no stake. */
  kind?: 'OFFICIAL' | 'COMMUNITY';
  /** C-08 v3: `name` is null when the request named none. */
  manager?: { address: string; holderId: string; name?: string | null } | null;
  stake?: {
    expected: string;
    received: string | null;
    transferStatus: string | null;
    /** XBIT-4400: the manager's other parked transfers (the stake's own excluded); absent from an older BFF. */
    additionalCount?: number;
    additionalTotal?: string;
  } | null;
  /** C-08 v2 (XBIT-4194): set once the application is approved or rejected. */
  review?: VaultApplicationReview | null;
  /** C-08 v2: set with CANCELLED. */
  cancelledReason?: string | null;
  /** C-06 / C-08 v3.2 (XBIT-4680): RFC 3339 UTC, when a community applicant accepted the create form's commitment checkbox; null on OFFICIAL requests and older records; absent from an older BFF. */
  commitmentAcceptedAt?: string | null;
  /**
   * The vault account's own HyperCore address (C-06 hlAddress): where the manager sends the stake (D-30 cl.2). Null
   * until the account exists (ACCOUNT_READY) or when the engine could not read it; absent from an older BFF.
   */
  hlAddress?: string | null;
}

/** The POST body. Optional fields are omitted, never sent as "". */
export interface OfficialVaultRequestInput {
  vaultId: string;
  name: string;
  strategy: string;
  feeRate: string;
  lockupDays: number;
  xHandle?: string;
  groupChatId?: string;
  /**
   * C-08 v2: who runs the vault — for an official vault the trading-team account (Module PRD v9 §14.4). Required for
   * every request; the address is the HyperCore address bound to that UID, the one the stake is sent from.
   */
  manager: { address: string; holderId: string; name: string };
  /** C-08 v2: the stake the manager undertakes to transfer, a decimal string at 6 dp (required by the engine). */
  stakeAmount: string;
}

export async function listOfficialVaultRequests(): Promise<OfficialVaultRequest[]> {
  const { json } = await httpClient(API_PATH, { method: 'GET' });
  return Array.isArray(json) ? (json as OfficialVaultRequest[]) : [];
}

/**
 * Idempotent on `vaultId` with equal terms while REQUESTED (design §5.1): after a
 * 503 VAULT_ONBOARDING_NOT_PUBLISHED, sending the SAME input again re-publishes.
 */
export async function requestOfficialVault(
  input: OfficialVaultRequestInput
): Promise<OfficialVaultRequest> {
  const { json } = await httpClient(API_PATH, { method: 'POST', body: JSON.stringify(input) });
  return json as OfficialVaultRequest;
}

/**
 * XBIT-4257 / XBIT-4366 (STORY-22 AC3, D-30 amendment 2026-10-02): record APPROVED and open the vault ACTIVE. No raise
 * window: approval is the only way a vault opens (AC15), and approving again replays the decision. A 409
 * VAULT_REVIEW_CONFLICT carries `review.recorded` (C-06): true means the decision stands.
 */
export async function approveVaultApplication(vaultId: string): Promise<OfficialVaultRequest> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/approve`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return json as OfficialVaultRequest;
}

/** XBIT-4257 (STORY-22 AC4): record REJECTED with the reason (1–500 characters, shown to the applicant), close the DRAFT row. */
export async function rejectVaultApplication(
  vaultId: string,
  reason: string
): Promise<OfficialVaultRequest> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return json as OfficialVaultRequest;
}

/**
 * XBIT-4240 (C-08 v3.1): the answer of a re-drive. `request` is the application as it now stands (back at the failed step's
 * predecessor, no failure); `wakeUpSent` false means the record moved but the wake-up was not published, so the consumer
 * picks it up on its own after about 10 minutes.
 */
export interface OfficialVaultRedrive {
  request: OfficialVaultRequest;
  fromStep: string;
  wakeUpSent: boolean;
}

/**
 * `POST /vault/official-vaults/:id/redrive`: move a FAILED official request back to the step that failed (revalidate and
 * account → Requested, catalog → Account ready), same vault id, same terms. A 409 names why not: VAULT_REDRIVE_NOT_FAILED
 * · _NOT_OFFICIAL · _STEP_UNKNOWN · _CONFLICT · _NAME_TAKEN, with the facts under `redrive`.
 */
export async function redriveOfficialVaultRequest(vaultId: string): Promise<OfficialVaultRedrive> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/redrive`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return json as OfficialVaultRedrive;
}

/** `GET /vault/faucet/bank` (XBIT-4348). Both fields are optional: an older BFF, or a stage with no faucet, omits them. */
export interface FaucetBankInfo {
  address?: string | null;
  /** `venueconst.Network` as the engine spells it. Only `"Testnet"` offers the action. */
  network?: string | null;
  /** XBIT-4400: the per-transfer ceiling (VAULT_FAUCET_MAX_AMOUNT); absent from an older BFF. */
  maxAmount?: string | null;
  /**
   * What the bank account can send now, read from the venue by the engine each time this is asked: a decimal string,
   * the perp withdrawable for a default account (USDC held only in spot reads "0.000000"). `null` = the venue read
   * failed (unknown, NOT zero); absent from an older BFF.
   */
  balance?: string | null;
}

/**
 * One stored faucet request (`GET /vault/official-vaults/:id/faucet-transfers`). The send reply is always DRAFT, so this
 * is the only place a FAILED request is visible. `failureCode` / `failureReason` are set only on a `FAUCET_FAILED` row;
 * the reason is the engine's curated text and is shown as sent.
 */
export interface FaucetTransferRecord {
  vaultId: string;
  userId: string;
  amount: string;
  txHash: string;
  /** FAUCET_DRAFT | FAUCET_SENT | FAUCET_RECORDED | FAUCET_FAILED */
  state: string;
  failureCode?: string | null;
  failureReason?: string | null;
  /** RFC 3339, UTC. */
  createdAt?: string | null;
}

/** The intended transfer, before a key is attached. */
export interface FaucetTransfer {
  vaultId: string;
  userId: string;
  amount: string;
}

/** The POST body. */
export interface SendFaucetTransferInput extends FaucetTransfer {
  /**
   * Minted in the browser at submit, so an operator double-click, an HTTP retry and a NATS redelivery inside
   * the engine all collapse into one transfer. A key minted per inbound request upstream would defend only
   * against the redelivery.
   */
  idempotencyKey: string;
}

export interface SendFaucetTransferResult {
  vaultId: string;
  userId: string;
  amount: string;
  /** ALWAYS "" from this mutation: a Hyperliquid `usdSend` reply carries no hash, so it reaches the system later from the venue ledger. Never render it. */
  txHash: string;
  /** ALWAYS "DRAFT" from this mutation: it records intent and publishes, and a consumer sends and records. Never branch on it. */
  state: string;
  /** false: this idempotency key was already applied, so nothing was sent a second time. A calm outcome, not an error. */
  applied: boolean;
}

/** The vault's last 20 faucet requests, newest first, in every state. 403 off the unstable stage or without `vault:faucet` read. */
export async function getFaucetTransfers(vaultId: string): Promise<FaucetTransferRecord[]> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/faucet-transfers`, {
    method: 'GET',
  });
  return json as FaucetTransferRecord[];
}

/** The bank account the faucet sends from. Carries no secret; the response is `{address, network}` only. */
export async function getFaucetBankInfo(): Promise<FaucetBankInfo> {
  const { json } = await httpClient(`${baseUrl}/vault/faucet/bank`, { method: 'GET' });
  return json as FaucetBankInfo;
}

/** XBIT-4348. The vault id is in the path; the body is the credited user, the amount and the idempotency key. */
export async function sendTestnetFaucetTransfer({
  vaultId,
  ...body
}: SendFaucetTransferInput): Promise<SendFaucetTransferResult> {
  const { json } = await httpClient(`${API_PATH}/${encodeURIComponent(vaultId)}/faucet`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return json as SendFaucetTransferResult;
}
